import { defineStore } from 'pinia'
import browser from 'webextension-polyfill'

import {
  EXTENSION_HEIGHT_PX,
  isFirefox,
  isLinuxOs,
  isWindowsOs,
  POPUP_REDUCED_HEIGHT_PX,
  POPUP_WIDTH_PX,
  TOAST_HEIGHT_PX,
} from '@/shared/popup'
import { saveLastState } from '@/popup/storage/Database'
import { Toast } from '@/popup/utils/toasts'
import { LastPopupState, Tab } from '@/shared/models/Profile'

export type UIState = {
  type: browser.Windows.WindowType
  currentTab?: Tab
  isWindowsOs: boolean
  isLinuxOs: boolean
  isFirefox: boolean
  toast?: Toast
  flipped: boolean
  scrollRefresh?: number
  scrollPosition: {
    activityView: number
  }
  isExternalPopup: boolean
}

export const useUIStore = defineStore('ui', {
  state: () =>
    ({
      type: 'normal',
      isWindowsOs: false,
      isFirefox: false,
      toast: undefined,
      flipped: false,
      currentTab: undefined,
      scrollRefresh: undefined,
      scrollPosition: { activityView: 0 },
      isExternalPopup: false,
    }) as UIState,
  getters: {
    isPopup: state => {
      return state.type === 'popup'
    },
  },
  actions: {
    setExternalPopup(value: boolean) {
      this.isExternalPopup = value
    },
    async init(
      isUnlocked: boolean,
      lastPopupState: LastPopupState | undefined,
      hasApprovals: boolean,
      currentWindow: browser.Windows.Window
    ) {
      const homeEdit = lastPopupState?.homeEdit
      const path = lastPopupState?.path || '/'

      await this.initCrossBrowserOS()
      this.type = currentWindow.type || 'normal'

      if (homeEdit?.tab) {
        this.setCurrentTab(homeEdit.tab, {})
      }

      this.updateHeight(path, isUnlocked, hasApprovals)
    },
    async initCrossBrowserOS() {
      this.isFirefox = isFirefox()
      if (this.isFirefox) {
        document.body.style.width = `${POPUP_WIDTH_PX}px`
      }
      this.isWindowsOs = await isWindowsOs()
      this.isLinuxOs = await isLinuxOs()
    },
    updateHeight(path: string, isUnlocked: boolean, hasApprovals: boolean) {
      const toastPx = this.toast ? TOAST_HEIGHT_PX : 0
      if (this.isPopup) {
        document.body.style.height = 'auto'
        document.body.style.minHeight = `${EXTENSION_HEIGHT_PX}px`
      } else if (
        !this.currentTab &&
        isUnlocked &&
        !hasApprovals &&
        (path === '/' || path === '/home')
      ) {
        document.body.style.height = `${POPUP_REDUCED_HEIGHT_PX + toastPx}px`
      } else {
        document.body.style.height = `${EXTENSION_HEIGHT_PX}px`
      }
    },
    setCurrentTab(tab: Tab | undefined, changes: { [key: string]: any }) {
      this.currentTab = tab
      this.updateHeight('/', true, false)
      saveLastState({
        homeEdit: { tab },
        ...changes,
      })
    },
    setCardFlipped(value: boolean) {
      this.flipped = value
    },
    setToast(toast?: Toast) {
      this.toast = toast
      if (document.body.style.height !== `${EXTENSION_HEIGHT_PX}px`) {
        const toastPx = toast ? TOAST_HEIGHT_PX : 0
        document.body.style.height = `${POPUP_REDUCED_HEIGHT_PX + toastPx}px`
      }
    },
    setScrollRefresh() {
      this.scrollRefresh = Date.now()
    },
  },
})
